import React, { useCallback } from 'react';
import type { FC } from 'react';
import { isType, match } from 'variant';
import { ConfirmInput, Link, Spinner, useAsync, Box } from '@wix/cli-ui-kit';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { useWixCliAppBi } from '../../bi/index.js';

interface RequiredApp {
  id: string;
  name: string;
}

interface AppRequirementProps {
  appId: string;
  requiredApps: RequiredApp[];
  onSubmit: (requiredApps: string[]) => void;
}

export const AppRequirement: FC<AppRequirementProps> = ({
  appId,
  requiredApps,
  onSubmit,
}) => {
  const bi = useWixCliAppBi();
  const { t } = useTranslation();
  const { getRequiredApps } = useDevCenterClient();
  const { reportError } = useErrorReporter();

  const { status } = useAsync(async () => {
    const existingRequiredApps = await getRequiredApps(appId).catch(
      (err: unknown) => {
        reportError(err);
        return requiredApps;
      }
    );

    const appsToRequire = requiredApps.filter((requiredApp) =>
      existingRequiredApps.every(
        (existingRequiredApp) => existingRequiredApp.id !== requiredApp.id
      )
    );

    if (appsToRequire.length === 0) {
      onSubmit([]);
    }

    return appsToRequire;
  }, []);

  const handleSubmit: (res: boolean) => void = useCallback(
    (shouldRequire) => {
      if (isType(status, 'Success')) {
        bi.cliFlowStepAnswered({
          question: t('generate_command.required_app', {
            appsNames: status.result
              .map((requiredApp) => requiredApp.name)
              .join(', '),
          }),
          questionKey: 'generate_command.required_app',
          answer: String(shouldRequire),
          additionalInfo: {
            requiredAppsIds: status.result.map((app) => app.id),
          },
        });

        if (!shouldRequire) {
          onSubmit([]);
        } else {
          onSubmit(status.result.map((app) => app.id));
        }
      }
    },
    [bi, onSubmit, t, status]
  );

  return (
    <>
      {match(status, {
        Success: ({ result: appsToRequire }) =>
          appsToRequire.length > 0 && (
            <Box marginBottom={1}>
              <ConfirmInput
                label={t('generate_command.required_app', {
                  appsNames: appsToRequire
                    .map((requiredApp) => requiredApp.name)
                    .join(', '),
                })}
                info={
                  <Trans
                    i18nKey="generate_command.required_app.description"
                    values={{
                      appsNames: appsToRequire
                        .map((requiredApp) => requiredApp.name)
                        .join(', '),
                    }}
                    // https://dev.wix.com/docs/build-apps/launch-your-app/market-listing/add-your-app-audience-info
                    components={[<Link url="https://wix.to/YAydHiJ" />]}
                  />
                }
                onSubmit={handleSubmit}
              />
            </Box>
          ),
        Loading: () => <Spinner />,
        Error: () => null,
      })}
    </>
  );
};
